// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

// SERVICES
import { TokenStorageService } from '../../../../../app/state/core/services/token-storage/token-storage.service';
import { AuthService } from '../services/auth.service';


// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import * as businessActions from '../../business/store/actions';
import { State } from '../../../root-state';
import * as authActions from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { AUTH_METHOD_CONSTANTS, AUTH_NGRX_CONSTANTS } from '../../../../constants/ngrx-store/auth';


/**
 * This effect is for listen auth actions, execute auth service call and dispatch in auth reducer.
 * For auth api call purpose.
 *
 * @author Pratik Shihora <pratik@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 16/03/2020 (Pratik Shihora <pratik@saeculumsolutions.com>) auth effect created
 * Date: 15/04/2020 (Pratik Shihora <pratik@saeculumsolutions.com>) auth login effect created
 */
@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private tokenStorageService: TokenStorageService,
    private authService: AuthService) { }

  /**
		  * Auth Effect
		  * @param type : AUTH_COMMON action name.
		  * @param payload : object to identify common auth method base on module and method.
		  * @returns Empty action dispatch to reducer.
		  */
  authCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<authActions.AuthCommonAction>(authActions.ActionTypes.AUTH_COMMON),
    withLatestFrom(this.store),
    switchMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = AUTH_NGRX_CONSTANTS[action.payload.module];

      // avoid service call if data already exists in store.
      if (state[common.STATE]) {
        return of(new globalActions.EmptyAction({}));
      }
      // only sending payload data

      return this.authService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            // throwing an error on if the status is not 200
            let actions: any[] = [];

            if (action.payload.callback) {
              action.payload.callback({ success: true, data });
            }
            // dispatch auth success action
            actions = [new authActions.AuthCommonSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup
              actions.push(new globalActions.PopupNotificationAction({
                actionType: authActions.ActionTypes.AUTH_COMMON, type: ModalType.SUCCESS,
                callback: action.payload.callback, title: common.TITLE,
                message: `${common.SUCCESS}`,
              }));
            }

            return actions;
          }),
          catchError(error => {
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: authActions.ActionTypes.AUTH_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message,
              callback: action.payload.callback, error: (error || error.error),
              //   code: common.ERROR, // `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }));
          }));
    }),
  ));

  /**
		 * Login Effect
		 * @param type : LOGIN action name.
		 * @param payload : object to identify common auth method base on module and method.
		 * @returns Empty action dispatch to reducer.
		 */
  loginEffect$ = createEffect(() => this.actions$.pipe(
    ofType<authActions.LoginAction>(authActions.ActionTypes.LOGIN),
    withLatestFrom(this.store),
    switchMap(([action]): Observable<any> => {
      // get common constant base action module and method.
      const common = AUTH_NGRX_CONSTANTS[AUTH_METHOD_CONSTANTS.LOGIN];

      return this.authService.login(action.payload.data)
        .pipe(
          mergeMap((data) => {
            let actions: any[] = [];

            // dispatch login success action
            actions = [new authActions.LoginSuccessAction({ data: data.data.users })];
            this.tokenStorageService.setAccessToken(data.data.users.token);
            if (data && data.data && data.data.users.businessViewModel && data.data.users.businessViewModel.business_token) {
              actions.push(new businessActions.BusinessSuccessAction({ data: data.data.users.businessViewModel }));
              this.tokenStorageService.setBusinessToken(data.data.users.businessViewModel.business_token);
            }
            // callback for stop loader and proceed next
            action.payload.callback({ success: true, data: data.data });

            return actions;
          }),
          catchError(error => {
            console.log(error)
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: authActions.ActionTypes.LOGIN, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: error.error,
              //   code: common.ERROR, //`ERROR.${error.error.code || 'INVALID_CREDENTIALS'}`,
              title: common.TITLE,
            }))
          }));
    }),
  ));

  /**
   * Logout Effect
   * @param type : LOGOUT action name.
   * @param payload : object to identify common auth method base on module and method.
   * @returns Empty action dispatch to reducer.
   */
  logoutEffect$ = createEffect(() => this.actions$.pipe(
    ofType<authActions.LogoutAction>(authActions.ActionTypes.LOGOUT),
    withLatestFrom(this.store),
    switchMap(([action]): Observable<any> => {
      // get common constant base action module and method.
      const common = AUTH_NGRX_CONSTANTS[AUTH_METHOD_CONSTANTS.LOGOUT];

      return this.authService.logout(action.payload.data)
        .pipe(
          mergeMap((data) => {
            let actions: any[] = [];

            // dispatch logout success action
            actions = [new authActions.LogoutSuccessAction({})];

            // dispatch cleear all reducer on logout action
            actions = [new authActions.ClearReducerAction({})];

            // callback for stop loader and proceed next
            action.payload.callback && action.payload.callback({ success: true, data: data.data });

            return actions;
          }),
          catchError(error => {
            error.error = error.error || {}
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: authActions.ActionTypes.LOGOUT, type: ModalType.ERROR,
              message: error && error.error && error.error.message,
              callback: action.payload.callback,
              //   code: common.ERROR, //`ERROR.${error.error.code || 'INVALID_CREDENTIALS'}`,
              title: common.TITLE,
            }))
          }));
    }),
  ));

}
